import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import { toWidget } from "@ckeditor/ckeditor5-widget/src/utils";
import Widget from "@ckeditor/ckeditor5-widget/src/widget";
import Command from "@ckeditor/ckeditor5-core/src/command";

const downcastConversion = (editor) => ({
  model: "embeddedVideo",
  view: (modelElement, { writer: viewWriter }) => {
    const videoSrc = modelElement.getAttribute("src");
    const repositoryItemId = modelElement.getAttribute("repository-item-id");
    const contentRepositoryAttachableId = modelElement.getAttribute(
      "content-repository-attachable-id"
    );

    const videoElement = viewWriter.createContainerElement("video", {
      class: "embedded-video",
      "data-src": videoSrc,
      "data-repository-item-id": repositoryItemId,
      "data-content-repository-attachable-id": contentRepositoryAttachableId,
      controls: true,
    });

    const videoSourceElement = viewWriter.createRawElement("source", {
      src: videoSrc,
    });

    viewWriter.insert(
      viewWriter.createPositionAt(videoElement, 0),
      videoSourceElement
    );
    const videoWidget = toWidget(videoElement, viewWriter, {
      label: "Embedded Video Widget",
    });

    return videoWidget;
  },
});

const upcastConversion = {
  model: (viewElement, { writer: modelWriter }) => {
    return modelWriter.createElement("embeddedVideo", {
      src: viewElement.getAttribute("data-src"),
      "repository-item-id": viewElement.getAttribute("data-repository-item-id"),
      "content-repository-attachable-id": viewElement.getAttribute(
        "data-content-repository-attachable-id"
      ),
    });
  },
  view: {
    name: "video",
    classes: "embedded-video",
  },
};

export default class EmbedVideo extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      "insertEmbeddedVideo",
      new InsertEmbeddedVideoCommand(this.editor)
    );

    this.editor.on("addVideo", (eventInfo, video) => {
      this.editor.execute("insertEmbeddedVideo", {
        video,
      });
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("embeddedVideo", {
      isObject: true,
      allowWhere: "$block",
      allowAttributes: [
        "src",
        "id",
        "repository-item-id",
        "content-repository-attachable-id",
      ],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for("upcast").elementToElement(upcastConversion);
    conversion
      .for("dataDowncast")
      .elementToElement(downcastConversion(this.editor));
    conversion
      .for("editingDowncast")
      .elementToElement(downcastConversion(this.editor));
  }
}

class InsertEmbeddedVideoCommand extends Command {
  execute({ video }) {
    this.editor.model.change((writer) => {
      const videoElement = createEmbeddedVideo(writer, video);
      this.editor.model.insertContent(videoElement);
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      "embeddedVideo"
    );

    this.isEnabled = allowedIn !== null;
  }
}

function createEmbeddedVideo(writer, video) {
  const embeddedVideo = writer.createElement("embeddedVideo", {
    src: video?.url,
    "repository-item-id": video?.repositoryItemId,
    "content-repository-attachable-id": video?.contentRepositoryAttachableId,
  });
  return embeddedVideo;
}
